/**
 * Sortable table. Every table in the app sorts on every column, and every column can be
 * dragged wider or narrower by the handle on its header's right edge; the widths a viewer
 * sets are remembered per table in this browser (ui/columnWidths.ts).
 *
 * Cell appearance comes from classes in styles/base.css, never inline styles: an inline
 * background beats every stylesheet rule, which is what silently killed the row hover
 * in the first version of this component.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react'
import type { Definition } from '@/measures/definitions'
import { Info } from './Info'
import { useUrlState, text } from '@/ui/urlState'
import { MIN_COL_WIDTH, readColumnWidths, writeColumnWidths, type ColumnWidths } from '@/ui/columnWidths'
import { PersonBadge } from '@focalpointcare/review-drawer'

export interface Column<T> {
  key: string
  header: string
  /** Sort value. Null sorts last regardless of direction. */
  sort?: (row: T) => number | string | null
  render: (row: T) => ReactNode
  align?: 'left' | 'right'
  /**
   * Fixed width. Set one on every column of a wide table so it stops reflowing. It is the
   * default: a viewer can drag the column to another width, which is then remembered.
   */
  width?: number
  /** Scored metrics sit on a shaded band. */
  band?: boolean
  /** Context columns are muted — they are not part of the score. */
  dim?: boolean
  /** Renders a 1px divider before this column. */
  divider?: boolean
  /** Pins the column to the left edge while the rest scrolls. */
  frozen?: boolean
  /** Shown on the header, for a column whose name needs a sentence. */
  title?: string
  /**
   * The measure's definition, behind an "i" after the header (measures/definitions.ts).
   * When set, the header drops its native title: two popups on one hover is noise.
   */
  help?: Definition
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  initialSort,
  initialDir = 'desc',
  maxRows,
  maxHeight,
  empty = 'Nothing to show.',
  sortParam,
  groups,
  selectedKey,
  storageKey,
}: {
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  initialSort?: string
  initialDir?: 'asc' | 'desc'
  maxRows?: number
  /**
   * Caps the table's height and scrolls inside it.
   *
   * This is also what makes the sticky header real. `overflow-x: auto` forces
   * `overflow-y` to `auto`, so the wrapper — not the page — becomes the nearest scroll
   * container; without a height it never scrolls vertically and `position: sticky` on
   * the header has nothing to stick to.
   */
  maxHeight?: number | string
  empty?: string
  /**
   * Keep the sort in the URL under this name (`?sort=cpc.desc`), so Back from a row's
   * drill returns to the same order and a copied link keeps it. One name per table on a
   * page. Without it the sort is the table's own and resets when the page is left.
   */
  sortParam?: string
  /** The row a page's selection holds, by `rowKey`: drawn picked (aria-selected). */
  selectedKey?: string | null
  /**
   * Names the table for the column widths a viewer drags it to, remembered in this
   * browser. One name per table across the app (`page.table`), so two tables never share
   * widths. Without it the table falls back to its page and sort key.
   */
  storageKey?: string
  /**
   * Rows under collapsible header rows. Each header is a row of its own, built by
   * `summary` and drawn by the same columns, so the columns decide how a header reads.
   * Headers sort among themselves by the active column; rows sort within their group.
   * `maxRows` does not apply.
   */
  groups?: {
    of: (row: T) => string
    summary: (key: string, rows: T[]) => T
    collapsed: ReadonlySet<string>
    onToggle: (key: string) => void
  }
}) {
  const [localSort, setLocalSort] = useState({ key: initialSort, dir: initialDir })
  // Called whether or not the table keeps its sort in the URL: hooks cannot be
  // conditional. Without `sortParam` it reads an absent key and is never written.
  const [urlSort, setUrlSort] = useUrlState(
    sortParam ?? '_',
    `${initialSort ?? ''}.${initialDir}`,
    text,
  )
  const { key: sortKey, dir } = sortParam
    ? (() => {
        const at = urlSort.lastIndexOf('.')
        const d = urlSort.slice(at + 1)
        return {
          key: urlSort.slice(0, at) || undefined,
          dir: (d === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc',
        }
      })()
    : localSort
  const setSort = (next: { key: string | undefined; dir: 'asc' | 'desc' }) =>
    sortParam ? setUrlSort(`${next.key ?? ''}.${next.dir}`) : setLocalSort(next)
  const wrapRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const [scrolled, setScrolled] = useState(false)
  const [more, setMore] = useState(false)

  /*
   * Column widths. `sized` holds only the columns the viewer resized. While there are any,
   * the table switches to `table-layout: fixed` (the only layout that lets a column be
   * narrower than its content) and every other column is pinned at the width it had under
   * the normal layout (`natural`), so dragging one column never moves the others.
   */
  const tableId =
    storageKey ??
    `${window.location.pathname.split('/')[1] ?? ''}:${sortParam ?? columns.map((c) => c.key).join(',')}`
  const [stored, setStored] = useState(() => ({ id: tableId, widths: readColumnWidths(tableId) }))
  if (stored.id !== tableId) setStored({ id: tableId, widths: readColumnWidths(tableId) })
  const sized = stored.widths
  const sizedRef = useRef(sized)
  sizedRef.current = sized
  const isSized = columns.some((c) => sized[c.key] != null)
  const [natural, setNatural] = useState<ColumnWidths | null>(null)
  // Bumped when the natural widths need taking again: a column reset, a density change.
  const [remeasure, setRemeasure] = useState(0)
  // Set by a drag's start, which pins the widths itself before anything moves.
  const pinned = useRef(false)
  const wrapWidth = useRef(0)

  const syncScroll = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    setScrolled(el.scrollLeft > 1)
    setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 1)
  }, [])

  // Layout effect so the affordance is right on first paint, not one frame late.
  useLayoutEffect(() => {
    syncScroll()
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      syncScroll()
      // A wider or narrower card re-takes the natural widths; a taller one does not.
      if (Math.abs(el.clientWidth - wrapWidth.current) > 1) {
        if (wrapWidth.current) setRemeasure((n) => n + 1)
        wrapWidth.current = el.clientWidth
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [syncScroll, rows, columns])

  // The density setting rescales type and default widths without touching the data.
  useLayoutEffect(() => {
    const mo = new MutationObserver(() => setRemeasure((n) => n + 1))
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-density'] })
    return () => mo.disconnect()
  }, [])

  // Before paint, so a sized table never flashes at its normal layout first.
  useLayoutEffect(() => {
    if (!isSized) {
      setNatural(null)
      return
    }
    if (pinned.current) {
      pinned.current = false
      return
    }
    if (tableRef.current) setNatural(measureNatural(tableRef.current, columns, sizedRef.current))
    // `sized` is read through its ref on purpose: a drag must not re-take the others.
  }, [isSized, rows, columns, remeasure])

  const saveWidths = (next: ColumnWidths) => {
    sizedRef.current = next
    setStored({ id: tableId, widths: next })
    writeColumnWidths(tableId, next)
  }
  const setWidth = (key: string, w: number, save: boolean) => {
    const next = { ...sizedRef.current, [key]: Math.max(MIN_COL_WIDTH, Math.round(w)) }
    if (save) saveWidths(next)
    else {
      sizedRef.current = next
      setStored({ id: tableId, widths: next })
    }
  }
  /** The first resize pins every column where it is now, so none of the others jumps. */
  const beginResize = () => {
    if (isSized || !tableRef.current) return
    setNatural(headerWidths(tableRef.current, columns))
    pinned.current = true
  }
  const widthOf = (th: HTMLElement | null, key: string) =>
    sizedRef.current[key] ?? (th ? th.getBoundingClientRect().width : MIN_COL_WIDTH)

  const onResizeStart = (e: ReactPointerEvent<HTMLElement>, c: Column<T>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    const handle = e.currentTarget
    const start = widthOf(handle.closest('th'), c.key)
    const x0 = e.clientX
    beginResize()
    try {
      handle.setPointerCapture(e.pointerId)
    } catch {
      /* no live pointer to capture: moves still arrive while over the handle */
    }
    handle.dataset.dragging = 'true'
    const move = (ev: PointerEvent) => setWidth(c.key, start + ev.clientX - x0, false)
    const end = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', end)
      handle.removeEventListener('pointercancel', end)
      delete handle.dataset.dragging
      saveWidths(sizedRef.current)
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', end)
    handle.addEventListener('pointercancel', end)
  }
  const onResizeKey = (e: ReactKeyboardEvent<HTMLElement>, c: Column<T>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    e.stopPropagation()
    const start = widthOf(e.currentTarget.closest('th'), c.key)
    beginResize()
    const step = e.shiftKey ? 50 : 10
    setWidth(c.key, start + (e.key === 'ArrowRight' ? step : -step), true)
  }
  /** Double-click: this one column back to its default. */
  const resetColumn = (e: ReactMouseEvent, c: Column<T>) => {
    e.preventDefault()
    e.stopPropagation()
    const next = { ...sizedRef.current }
    delete next[c.key]
    saveWidths(next)
    setRemeasure((n) => n + 1)
  }
  const resetAll = () => {
    const next = { ...sizedRef.current }
    for (const c of columns) delete next[c.key]
    saveWidths(next)
  }

  const fixed = isSized && natural != null
  /** A column's width as drawn: the viewer's, else pinned where it lay, else its default. */
  const colWidth = (c: Column<T>): string | undefined => {
    const w = sized[c.key] ?? (fixed ? natural![c.key] : undefined)
    if (w != null) return `${w}px`
    /*
     * Widths are authored against the comfortable scale and multiplied by
     * --col-scale, so a denser or roomier type scale takes its columns with
     * it. Without this, Relaxed renders 15px mono inside a 98px column sized
     * for 13.5px and clips "$4,404.10" — bigger text that is harder to read.
     */
    return c.width ? `calc(${c.width}px * var(--col-scale, 1))` : undefined
  }
  const fixedWidth = fixed
    ? columns.reduce((sum, c) => sum + (sized[c.key] ?? natural![c.key] ?? MIN_COL_WIDTH), 0)
    : 0

  /**
   * A cell cut short by its column shows its full text on hover. Decided on the way in, so
   * only clipped cells get a tooltip, and never over a title the page set itself.
   */
  const titleClipped = (e: ReactMouseEvent) => {
    const t = e.target as HTMLElement
    const el = (t.closest('.td-trunc') ?? t.closest('td')) as HTMLElement | null
    if (!el || (el.hasAttribute('title') && !el.dataset.clipTitle)) return
    if (el.scrollWidth > el.clientWidth + 1) {
      el.title = el.textContent?.trim() ?? ''
      el.dataset.clipTitle = 'true'
    } else if (el.dataset.clipTitle) {
      el.removeAttribute('title')
      delete el.dataset.clipTitle
    }
  }

  const compare = useMemo(() => {
    const get = columns.find((c) => c.key === sortKey)?.sort
    if (!get) return null
    return (a: T, b: T) => {
      const av = get(a)
      const bv = get(b)
      // Nulls sort last in both directions — an absent value is not a small one.
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      const c =
        typeof av === 'string' || typeof bv === 'string'
          ? String(av).localeCompare(String(bv))
          : (av as number) - (bv as number)
      return dir === 'asc' ? c : -c
    }
  }, [columns, sortKey, dir])

  const sorted = useMemo(() => (compare ? [...rows].sort(compare) : rows), [rows, compare])

  const shown = maxRows && !groups ? sorted.slice(0, maxRows) : sorted

  /**
   * What the body draws: the rows, or under `groups` each header (`group` set) then its
   * rows unless it is collapsed.
   */
  const items = useMemo((): { row: T; group?: string }[] => {
    if (!groups) return shown.map((row) => ({ row }))
    const byKey = new Map<string, T[]>()
    for (const r of shown) {
      const k = groups.of(r)
      const list = byKey.get(k)
      if (list) list.push(r)
      else byKey.set(k, [r])
    }
    const heads = [...byKey].map(([key, list]) => ({ key, head: groups.summary(key, list), list }))
    if (compare) heads.sort((a, b) => compare(a.head, b.head))
    return heads.flatMap(({ key, head, list }) => [
      { row: head, group: key },
      ...(groups.collapsed.has(key) ? [] : list.map((row) => ({ row }))),
    ])
  }, [shown, groups, compare])

  const groupRow = (head: T, key: string) => {
    const toggle = () => groups!.onToggle(key)
    return (
      <tr
        key={`g:${key}`}
        className="dt-group"
        data-group={key}
        aria-expanded={!groups!.collapsed.has(key)}
        data-clickable="true"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        {columns.map((c) => (
          <td
            key={c.key}
            className={cls(c, 'td')}
            style={{ textAlign: c.align ?? 'left' }}
            data-col={c.key}
          >
            {c.render(head)}
          </td>
        ))}
      </tr>
    )
  }

  const toggle = (c: Column<T>) => {
    if (!c.sort) return
    if (sortKey === c.key) setSort({ key: c.key, dir: dir === 'asc' ? 'desc' : 'asc' })
    // A numeric column almost always wants its biggest value first.
    else setSort({ key: c.key, dir: c.align === 'right' ? 'desc' : 'asc' })
  }

  if (rows.length === 0) {
    return (
      <p className="body-note" style={{ color: 'var(--ink-3)', padding: '14px 16px' }}>
        {empty}
      </p>
    )
  }

  const cls = (c: Column<T>, kind: 'th' | 'td') =>
    [
      c.band ? 'band' : '',
      c.divider ? 'divider' : '',
      c.frozen ? 'frozen' : '',
      kind === 'td' && c.dim ? 'dim' : '',
    ]
      .filter(Boolean)
      .join(' ')

  return (
    <div className="dt-box" style={{ position: 'relative' }}>
      <div
        className="dt-wrap"
        ref={wrapRef}
        onScroll={syncScroll}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table
          ref={tableRef}
          className={`dt${scrolled ? ' is-scrolled' : ''}${fixed ? ' is-sized' : ''}`}
          style={fixed ? { tableLayout: 'fixed', width: fixedWidth } : undefined}
        >
          <colgroup>
            {columns.map((c) => {
              const w = colWidth(c)
              return <col key={c.key} style={w ? { width: w } : undefined} />
            })}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cls(c, 'th')}
                  data-sortable={c.sort ? 'true' : undefined}
                  aria-sort={
                    sortKey === c.key
                      ? dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggle(c)}
                  style={{ textAlign: c.align ?? 'left' }}
                  title={c.help ? undefined : (c.title ?? (c.sort ? `Sort by ${c.header}` : undefined))}
                >
                  {c.header}
                  <Info def={c.help} />
                  {c.sort && (
                    /*
                     * The idle chevron marks which headers are sortable, so it has to
                     * be visible. At opacity 0.45 it measured 1.9:1 — under the 3:1
                     * floor for a UI affordance and, in practice, invisible. The active
                     * one still reads louder because it is a filled ▲/▼ in accent ink
                     * against a hollow ↕, which is a difference of shape rather than of
                     * whether you can see it at all.
                     */
                    <span
                      aria-hidden="true"
                      style={{
                        marginLeft: 4,
                        color: sortKey === c.key ? 'inherit' : 'var(--ink-3)',
                      }}
                    >
                      {sortKey === c.key ? (dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  )}
                  <span
                    className="dt-resize"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${c.header} column`}
                    aria-valuenow={sized[c.key] ?? natural?.[c.key]}
                    aria-valuemin={MIN_COL_WIDTH}
                    tabIndex={0}
                    title="Drag to resize · double-click to reset"
                    onPointerDown={(e) => onResizeStart(e, c)}
                    onKeyDown={(e) => onResizeKey(e, c)}
                    onDoubleClick={(e) => resetColumn(e, c)}
                    // The header sorts on click; a drag or a double-click here must not.
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody onMouseOver={fixed ? titleClipped : undefined}>
            {items.map(({ row, group }) =>
              group !== undefined ? groupRow(row, group) : (
              <tr
                key={rowKey(row)}
                // Lets a review comment find this row again (src/comments/figures.ts).
                data-row-key={rowKey(row)}
                data-clickable={onRowClick ? 'true' : undefined}
                aria-selected={selectedKey != null && rowKey(row) === selectedKey ? true : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                // Reachable by Tab and opened with Enter, as the Ledger's clickable rows.
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          onRowClick(row)
                        }
                      }
                    : undefined
                }
              >
                {columns.map((c, i) => (
                  <td
                    key={c.key}
                    className={cls(c, 'td')}
                    style={{ textAlign: c.align ?? 'left' }}
                    data-col={c.key}
                    data-col-header={c.header}
                  >
                    {c.render(row)}
                    {/* Reviewers only: open comments about the person on this row. */}
                    {i === 0 && <PersonBadge name={rowKey(row).replace(/^p:/, '')} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {more && <div className="dt-more" aria-hidden="true" />}

      {isSized && (
        <button
          type="button"
          className="dt-reset"
          onClick={resetAll}
          title="Put every column of this table back to its default width"
        >
          Reset columns
        </button>
      )}

      {(maxRows && sorted.length > maxRows) || more ? (
        <p
          className="mono"
          style={{
            fontSize: 'var(--t-micro)',
            color: 'var(--ink-3)',
            margin: 0,
            padding: '8px 11px 2px',
          }}
        >
          {maxRows && sorted.length > maxRows
            ? `showing ${maxRows} of ${sorted.length}`
            : ''}
          {maxRows && sorted.length > maxRows && more ? ' · ' : ''}
          {more ? 'scroll right for more columns' : ''}
        </p>
      ) : null}
    </div>
  )
}

/** Each header's drawn width, by column key. */
function headerWidths<T>(table: HTMLTableElement, columns: Column<T>[]): ColumnWidths {
  const cells = table.tHead?.rows[0]?.cells
  const out: ColumnWidths = {}
  columns.forEach((c, i) => {
    out[c.key] = Math.max(MIN_COL_WIDTH, Math.round(cells?.[i]?.getBoundingClientRect().width ?? 0))
  })
  return out
}

/**
 * The widths the columns take under the normal (auto) layout with the viewer's widths
 * applied, read in one go by laying the table out that way for a moment and putting
 * React's styles straight back. A column reset to its default gets its natural width here.
 */
function measureNatural<T>(
  table: HTMLTableElement,
  columns: Column<T>[],
  sized: ColumnWidths,
): ColumnWidths {
  const cols = [...table.querySelectorAll<HTMLTableColElement>(':scope > colgroup > col')]
  const tableCss = table.style.cssText
  const colCss = cols.map((col) => col.style.cssText)
  table.style.tableLayout = 'auto'
  table.style.width = ''
  columns.forEach((c, i) => {
    const col = cols[i]
    if (!col) return
    col.style.width =
      sized[c.key] != null
        ? `${sized[c.key]}px`
        : c.width
          ? `calc(${c.width}px * var(--col-scale, 1))`
          : ''
  })
  const out = headerWidths(table, columns)
  table.style.cssText = tableCss
  cols.forEach((col, i) => (col.style.cssText = colCss[i]!))
  return out
}
